import { useMutation, useQueryClient } from 'react-query';
import { getKey as getUseFetchSearchesPaginatedKey } from 'v2/hooks/useFetchSearchesPaginated';
import {
  Activity,
  ActivityAssociation,
  DjangoListResponse,
  DrmEntityBackend,
  LinkEntityType,
} from 'v2/models';
import { getKey as getSearchDetailKey } from 'v2/queries/searches/useSearchDetails';
import { getKey as getSearchConversationCountKey } from 'v2/queries/useSearchConversationCount';
import { getKey as getInfiniteSearchActivitiesKey } from 'v2/queries/useInfiniteSearchActivities';

import restFactory from 'v2/utils/restFactory';

export type userType = 'internal' | 'external';

export interface participant {
  first_name?: string;
  last_name?: string;
  organization_name?: string;
  organization_category: string;
  job_title?: string;
  talkjs_id: string;
  user_type: userType;
  avatar_url?: string;
}

export interface ReadOnlyConversation {
  conversation_id: string;
  participants: participant[];
  last_message_timestamp: string;
  last_message: string;
  color: string;
  selected: boolean;
}

type TActivityToCreate = Omit<
  Activity,
  'id' | 'organization' | 'creator' | 'is_editable' | 'is_deletable'
>;

function getTableName(entity: LinkEntityType) {
  switch (entity.entity) {
    case DrmEntityBackend.CONTACT:
      return 'Contact';
    case DrmEntityBackend.DATASET:
      return 'DataProduct';
    case DrmEntityBackend.DATA_VENDOR:
      return 'DataVendor';
    default:
  }
  throw new Error(`unimplemented entity type ${entity.entity}`);
}

export function getLinkActivity(
  searchId: number,
  entity: LinkEntityType
): TActivityToCreate {
  const drmRelatedObject: ActivityAssociation = {
    name: 'linked DRM object',
    object_id: entity.id,
    table_name: getTableName(entity),
  };
  const searchRelatedObject: ActivityAssociation = {
    name: 'linked Search',
    object_id: searchId,
    table_name: 'Searches',
  };

  const result: TActivityToCreate = {
    type: 'link_created',
    description: 'link search with DRM entity',
    timestamp: new Date().toISOString(),
    system_generated: false,
    relationships: [drmRelatedObject, searchRelatedObject],
    title: 'Link a Searh with a DRM Entity',
  };

  return result;
}

/**
 * reates an activity of type link and associates a search and the object
 */
export function useAddSearchDRMLinkActivity(searchId: number) {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: async (submitData: {
      currentEntity: LinkEntityType;
      link: string;
    }) => {
      const activity = getLinkActivity(searchId, submitData.currentEntity);
      const [, myConversations] = await Promise.all([
        restFactory.post(`/dj/api/v1/activity/`, activity),
        restFactory.get<DjangoListResponse<ReadOnlyConversation>>(
          `/dj/api/v1/search/${searchId}/conversations/`
        ),
      ]);

      const message = submitData.link;
      if (myConversations.count === 0) {
        await restFactory.get(
          `/dj/api/v1/search/${searchId}/assign_to_reviewer/`,
          {
            action: 'assign',
          }
        );
        await restFactory.post('/conversations/create_reviewer_conversation', {
          search: searchId,
          message,
        });
      } else {
        await Promise.all(
          myConversations.results.map(async (convo) =>
            restFactory.post(`/dj/api/v1/conversations/add_message/`, {
              conversation_id: convo.conversation_id,
              message,
            })
          )
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(getSearchConversationCountKey(searchId));
      queryClient.invalidateQueries(getSearchDetailKey(searchId));
      queryClient.invalidateQueries([getUseFetchSearchesPaginatedKey()[0]]);
      queryClient.invalidateQueries(getInfiniteSearchActivitiesKey(searchId));
    },
  });

  return result;
}
